import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { WordReveal } from '@/components/site/WordReveal';
import { CITIES } from '@/data/cities';

/**
 * US state index with a hover-swapped photo pane. Doubles as the main
 * internal-link path into /web-development-company-<state>.
 */
export function CityStrip({ num = '04', className = '' }) {
  const [active, setActive] = useState(0);
  return (
    <section data-testid="city-strip" className={`relative py-[clamp(96px,12vw,180px)] border-t border-white/[0.08] ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
          <SectionLabel num={num} className="lg:pt-4">Where we work</SectionLabel>
          <WordReveal
            as="h2"
            text="Built in India. Shipping for Texas, Florida, California and beyond."
            accent="Texas, Florida, California"
            className="font-display font-semibold text-[clamp(38px,5.6vw,92px)] leading-[0.96] tracking-[-0.045em] max-w-[18ch]"
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-start">
          <ul className="border-t border-white/[0.08]">
            {CITIES.map((c, i) => (
              <Reveal as="li" key={c.slug} delay={i * 60} className="border-b border-white/[0.08]">
                <Link
                  to={`${c.path}/`}
                  data-testid={`city-card-${c.slug}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  className="grid grid-cols-[64px_1fr] sm:grid-cols-[80px_1fr_auto] items-center gap-x-4 py-[clamp(18px,2.2vw,28px)]"
                >
                  <span className="nd-mono !text-[11px] whitespace-nowrap">0{i + 1} / 0{CITIES.length}</span>
                  <span
                    className={`font-display font-semibold text-[clamp(40px,6.4vw,104px)] leading-[0.95] tracking-[-0.055em] transition-[color,transform] duration-500 [transition-timing-function:var(--ease-out)] ${
                      active === i ? 'text-white translate-x-3' : 'text-white lg:text-[#727284]'
                    }`}
                  >
                    {c.name}
                  </span>
                  <span className="col-start-2 sm:col-start-3 text-sm text-ink-muted sm:text-right">
                    Web &amp; app development in {c.name} →
                  </span>
                </Link>
              </Reveal>
            ))}
          </ul>
          <ClipReveal className="hidden lg:block sticky top-28 aspect-[4/5] rounded-[18px] overflow-hidden ring-1 ring-white/[0.08] bg-ink-surface" aria-hidden>
            {CITIES.map((c, i) => (
              <img
                key={c.slug}
                src={c.image.src}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] [transition-duration:1200ms] [transition-timing-function:var(--ease-out)] [filter:grayscale(.85)_contrast(1.05)_brightness(.8)] ${
                  active === i ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.08]'
                }`}
              />
            ))}
            <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(10,10,15,.7),transparent_50%),linear-gradient(160deg,rgba(75,159,255,.22),transparent_55%)]" />
            <div className="nd-mono absolute left-6 right-6 bottom-5 flex justify-between">
              <span className="text-white">{CITIES[active].name}</span>
              <span>United States</span>
            </div>
          </ClipReveal>
        </div>
      </div>
    </section>
  );
}
